/**
 * Facebook Pixel Utility Functions
 * Custom implementation to replace react-facebook-pixel package
 */

class FacebookPixel {
    constructor() {
        this.pixelId = null;
        this.isInitialized = false;
        this.isDebug = false;
    }

    /**
     * Initialize Facebook Pixel
     * @param {string} pixelId - Facebook Pixel ID
     * @param {Object} options - Configuration options
     * @param {boolean} options.autoConfig - Auto configure pixel
     * @param {boolean} options.debug - Enable debug mode
     */
    init(pixelId, options = {}) {
        if (this.isInitialized) {
            console.warn('Facebook Pixel already initialized');
            return;
        }

        this.pixelId = pixelId;
        this.isDebug = options.debug || false;

        // Load Facebook Pixel script if not already loaded
        this.loadPixelScript();

        // Initialize pixel
        if (typeof window !== 'undefined' && window.fbq) {
            window.fbq('init', pixelId);
            this.isInitialized = true;

            if (this.isDebug) {
                console.log('Facebook Pixel initialized with ID:', pixelId);
            }
        } else {
            console.error('Facebook Pixel script not loaded');
        }
    }

    /**
     * Load Facebook Pixel script dynamically
     */
    loadPixelScript() {
        if (typeof window === 'undefined') return;

        // Check if script already exists
        if (document.querySelector('script[src*="connect.facebook.net"]')) {
            return;
        }

        // Create script element
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';

        // Add script to head
        document.head.appendChild(script);

        // Initialize fbq function
        if (!window.fbq) {
            window.fbq = function () {
                window.fbq.callMethod
                    ? window.fbq.callMethod.apply(window.fbq, arguments)
                    : window.fbq.queue.push(arguments);
            };
            window.fbq.push = window.fbq;
            window.fbq.loaded = true;
            window.fbq.version = '2.0';
            window.fbq.queue = [];
        }
    }

    /**
     * Track page view
     */
    pageView() {
        if (!this.isInitialized) {
            console.warn('Facebook Pixel not initialized');
            return;
        }

        if (typeof window !== 'undefined' && window.fbq) {
            window.fbq('track', 'PageView');
            // Mirror as a custom event for EcomTech-specific reporting.
            this.trackCustom('EcPageView');

            if (this.isDebug) {
                console.log('Facebook Pixel: PageView tracked');
            }
        }
    }

    /**
     * Track custom event
     * @param {string} eventName - Name of the event
     * @param {Object} parameters - Event parameters
     */
    track(eventName, parameters = {}) {
        if (!this.isInitialized) {
            console.warn('Facebook Pixel not initialized');
            return;
        }

        if (typeof window !== 'undefined' && window.fbq) {
            window.fbq('track', eventName, parameters);

            if (this.isDebug) {
                console.log(
                    'Facebook Pixel: Event tracked',
                    eventName,
                    parameters
                );
            }
        }
    }

    /**
     * Track a CUSTOM event (uses fbq('trackCustom', ...) — the correct API for
     * non-standard event names such as the Ec* events).
     * @param {string} eventName - Name of the custom event
     * @param {Object} parameters - Event parameters
     */
    trackCustom(eventName, parameters = {}) {
        if (!this.isInitialized) {
            console.warn('Facebook Pixel not initialized');
            return;
        }

        if (typeof window !== 'undefined' && window.fbq) {
            window.fbq('trackCustom', eventName, parameters);

            if (this.isDebug) {
                console.log(
                    'Facebook Pixel: Custom event tracked',
                    eventName,
                    parameters
                );
            }
        }
    }

    /**
     * Track purchase event
     * @param {Object} parameters - Purchase parameters
     * @param {number} parameters.value - Purchase value
     * @param {string} parameters.currency - Currency code
     */
    trackPurchase(parameters = {}) {
        this.track('Purchase', parameters);
        this.trackCustom('EcPurchase', parameters);
    }

    /**
     * Track add to cart event
     * @param {Object} parameters - Add to cart parameters
     */
    trackAddToCart(parameters = {}) {
        this.track('AddToCart', parameters);
    }

    /**
     * Track initiate checkout event
     * @param {Object} parameters - Checkout parameters
     */
    trackInitiateCheckout(parameters = {}) {
        this.track('InitiateCheckout', parameters);
    }

    /**
     * Track lead event
     * @param {Object} parameters - Lead parameters
     */
    trackLead(parameters = {}) {
        this.track('Lead', parameters);
    }

    /**
     * Track complete registration event
     * @param {Object} parameters - Registration parameters
     */
    trackCompleteRegistration(parameters = {}) {
        // Meta requires value (> 0) and currency on CompleteRegistration
        const params = {
            value: 1,
            currency: 'BDT',
            ...parameters,
        };
        this.track('CompleteRegistration', params);
        this.trackCustom('EcCompleteRegistration', params);
    }

    /**
     * Track search event
     * @param {Object} parameters - Search parameters
     */
    trackSearch(parameters = {}) {
        this.track('Search', parameters);
    }

    /**
     * Track view content event
     * @param {Object} parameters - View content parameters
     */
    trackViewContent(parameters = {}) {
        this.track('ViewContent', parameters);
    }

    /**
     * Track add payment info event
     * @param {Object} parameters - Payment info parameters
     */
    trackAddPaymentInfo(parameters = {}) {
        this.track('AddPaymentInfo', parameters);
    }

    /**
     * Get pixel ID
     * @returns {string|null} - Pixel ID
     */
    getPixelId() {
        return this.pixelId;
    }

    /**
     * Check if pixel is initialized
     * @returns {boolean} - Initialization status
     */
    isPixelInitialized() {
        return this.isInitialized;
    }

    /**
     * Reset pixel (for testing purposes)
     */
    reset() {
        this.pixelId = null;
        this.isInitialized = false;
        this.isDebug = false;
    }
}

// Create singleton instance
const facebookPixel = new FacebookPixel();

// Export both the class and the singleton instance
export default facebookPixel;
export { FacebookPixel };

// Common event tracking functions for easy use
export const trackPageView = () => facebookPixel.pageView();
export const trackPurchase = (parameters) =>
    facebookPixel.trackPurchase(parameters);
export const trackAddToCart = (parameters) =>
    facebookPixel.trackAddToCart(parameters);
export const trackInitiateCheckout = (parameters) =>
    facebookPixel.trackInitiateCheckout(parameters);
export const trackLead = (parameters) => facebookPixel.trackLead(parameters);
export const trackCompleteRegistration = (parameters) =>
    facebookPixel.trackCompleteRegistration(parameters);
export const trackSearch = (parameters) =>
    facebookPixel.trackSearch(parameters);
export const trackViewContent = (parameters) =>
    facebookPixel.trackViewContent(parameters);
export const trackAddPaymentInfo = (parameters) =>
    facebookPixel.trackAddPaymentInfo(parameters);
export const trackCustomEvent = (eventName, parameters) =>
    facebookPixel.track(eventName, parameters);
export const trackCustom = (eventName, parameters) =>
    facebookPixel.trackCustom(eventName, parameters);
